// Favorites service
// - Responsible for CRUD operations against the favorite_recipes table
// - Favorites are scoped to a household so all members can view and contribute
// - Full recipe data is stored in the `recipe_data` JSONB column so that
//   viewing a favorite never requires a call to the Spoonacular API

import { type FavoriteRecipe, supabase } from "../lib/supabase";
import type { Recipe, RecipeDetails } from "./recipeService";

export type { FavoriteRecipe };

export const favoritesService = {
  // Fetch all favorite recipes for a household
  async getFavorites(householdId: string): Promise<FavoriteRecipe[]> {
    const { data, error } = await supabase
      .from("favorite_recipes")
      .select(
        "id, household_id, added_by, recipe_id, title, image, recipe_data, created_at",
      )
      .eq("household_id", householdId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as FavoriteRecipe[];
  },

  // Add a recipe to the household's favorites.
  // Stores the full RecipeDetails payload so no future API call is needed.
  async addFavorite(
    recipe: Recipe,
    details: RecipeDetails,
    householdId: string,
    userId: string,
  ): Promise<FavoriteRecipe> {
    const { data, error } = await supabase
      .from("favorite_recipes")
      .insert([
        {
          household_id: householdId,
          added_by: userId,
          recipe_id: recipe.id,
          title: recipe.title,
          image: recipe.image ?? null,
          recipe_data: details,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as FavoriteRecipe;
  },

  // Remove a favorite recipe by its row id
  async removeFavorite(id: string): Promise<void> {
    const { error } = await supabase
      .from("favorite_recipes")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};
